import React, { useEffect, useRef, useCallback } from 'react';

const StarField = () => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Move Star class definition outside useEffect and memoize its creation
  const createStarClass = useCallback(() => {
    return class Star {
      constructor(canvas) {
        this.canvas = canvas;
        this.reset();
      }

      reset() {
        this.x = Math.random() * this.canvas.width;
        this.y = Math.random() * this.canvas.height;
        this.size = Math.random() * 2;
        this.opacity = Math.random();
        this.twinkleSpeed = 0.03 + Math.random() * 0.03;
        this.twinkleDirection = 1;
      }

      update() {
        this.opacity += this.twinkleSpeed * this.twinkleDirection;
        
        if (this.opacity >= 1) {
          this.opacity = 1;
          this.twinkleDirection = -1;
        } else if (this.opacity <= 0.2) {
          this.opacity = 0.2;
          this.twinkleDirection = 1;
        }
      }

      draw(ctx) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
        ctx.fill();
      }
    };
  }, []); // Empty dependency array since this definition doesn't depend on any props or state

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const stars = [];
    const numStars = 200;
    const Star = createStarClass(); // Get the Star class inside the effect

    const updateCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      
      stars.length = 0;
      for (let i = 0; i < numStars; i++) {
        stars.push(new Star(canvas));
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      stars.forEach(star => {
        star.update();
        star.draw(ctx);
      });
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [createStarClass]); // Add createStarClass to the dependency array

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full z-0"
      style={{ background: 'transparent' }}
    />
  );
};

export default StarField;